import { T, useGT } from 'gt-react';
import { useNavigate } from '@tanstack/react-router';
import { TrashIcon } from '@phosphor-icons/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { getInvoiceStatusBadge } from '@/components/accounts-receivable/invoice-status-badge';
import { Currency } from '@/components/common/Currency';
import type { ArPaymentApplicationEntry } from '@erp/rpc';

interface ArPaymentAllocationsProps {
  applications: ArPaymentApplicationEntry[];
  /** When true, manually-created allocations (allocationSource "user") show a remove control. */
  editable?: boolean;
  onRemove?: (applicationId: string) => void;
  removingId?: string | null;
}

export function ArPaymentAllocations({
  applications,
  editable = false,
  onRemove,
  removingId,
}: ArPaymentAllocationsProps) {
  const gt = useGT();
  const navigate = useNavigate();

  const handleInvoiceClick = (invoiceId: string) => {
    navigate({
      to: '/accounts-receivable/invoices/$arInvoiceId',
      params: { arInvoiceId: invoiceId },
      viewTransition: true,
    });
  };

  const totalApplied = applications.reduce((sum, app) => {
    const amount = app.appliedAmount ? Number(app.appliedAmount) : 0;
    return sum + (Number.isNaN(amount) ? 0 : amount);
  }, 0);

  if (applications.length === 0) {
    return (
      <div className="py-4 text-sm text-muted-foreground text-center">
        <T>No invoice allocations</T>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <T context="table column header for invoice number">Invoice #</T>
            </TableHead>
            <TableHead>
              <T context="table column header for applied payment amount">
                Applied Amount
              </T>
            </TableHead>
            <TableHead>
              <T context="table column header for invoice balance">
                Invoice Balance
              </T>
            </TableHead>
            <TableHead>
              <T context="table column header for invoice status">
                Invoice Status
              </T>
            </TableHead>
            {editable && <TableHead className="w-10" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {applications.map((app) => {
            const removable = editable && app.allocationSource === 'user';
            return (
              <TableRow
                key={app.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleInvoiceClick(app.invoiceId)}
              >
                <TableCell>
                  <span className="cursor-pointer text-primary hover:underline text-sm font-medium">
                    {app.invoice?.invoiceNumber ?? '—'}
                  </span>
                </TableCell>
                <TableCell>
                  <Currency
                    value={app.appliedAmount}
                    className="text-sm font-semibold"
                    fallback="—"
                  />
                </TableCell>
                <TableCell>
                  <Currency
                    value={app.invoice?.balanceDue ?? null}
                    className="text-sm text-muted-foreground"
                    fallback="—"
                  />
                </TableCell>
                <TableCell>
                  {app.invoice?.status
                    ? getInvoiceStatusBadge(app.invoice.status)
                    : '—'}
                </TableCell>
                {editable && (
                  <TableCell>
                    {removable && onRemove && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        disabled={removingId === app.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove(app.id);
                        }}
                        aria-label={gt('Remove allocation', {
                          context: 'a11y label, remove payment allocation',
                        })}
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <div className="flex justify-end px-2 py-1 text-sm font-semibold text-muted-foreground border-t">
        <T context="label for total applied payment amount">Total Applied:</T>{' '}
        <Currency value={totalApplied} className="ml-1 text-foreground" />
      </div>
    </div>
  );
}
